<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'PaymentsFilters',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'submit']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const createAccountGrups = (...args) => {
    const [accounts] = args;
    const showInactiveAccounts = item.value.showInactiveAccounts;

    return _.chain(accounts)
      .groupBy((p) => p.owner.id)
      .map((items, owner_id) => ({
        id: owner_id,
        name: items[0].owner.name,
        items: !showInactiveAccounts
          ? _.filter(items, { inactive: false })
          : items,
      }))
      .filter((p) => p.items.length)
      .value();
  };

  const item = ref({
    date_from: null,
    date_to: null,
    account: null,
    showInactiveAccounts: false,
  });
  const accounts = ref(null);
  const accountGroups = ref(null);
  const ux = ref([]);
  const b_5962862420439144_style = computed(() => ({
    width: '500px',
  }));

  const $data = computed(() => ({
    item: item.value,
    accounts: accounts.value,
    accountGroups: accountGroups.value,
    ux: ux.value,
    b_5962862420439144_style: b_5962862420439144_style.value,
  }));

  const $$load_accounts = () => {
    return $fn.value
      .fetch('list_account')
      .then((resp) =>
        _.orderBy(
          resp.data,
          ['inactive', 'account_type', 'name'],
          ['asc', 'desc', 'asc']
        )
      );
  };
  const $$load_accountGroups = () => {
    return createAccountGrups($data.value.accounts);
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item', cache: { type: 'local', key: 'PaymentsFilters' } },
      { name: 'accounts' },
      { name: 'accountGroups', dependsOn: ['accounts'] },
      { name: 'ux' },
      { name: 'b_5962862420439144_style', nodeId: '5962862420439144' },
    ],
    loaders: { accounts: $$load_accounts, accountGroups: $$load_accountGroups },
    ux: {},
    dataRefs: { item, accounts, accountGroups, ux, b_5962862420439144_style },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const selectedAccountType = computed(() => {
    if (loaded.value) {
      return item.value.account && item.value.account.account_type;
    }
    return null;
  });
  const selectedAccount = computed(() => {
    if (loaded.value) {
      return _.isObject(item.value.account) ? item.value.account : null;
    }
    return null;
  });

  const act_2248226175642056_emit_dfe1a6vsv_getActionArgs = async ($event) => {
    return {
      event: 'submit',
      value: { ...item.value, account: item.value.account?.id },
    };
  };
  const inst_evh_2248226175642056_updateModelValue_2szzvz5ja_getWhen = async (
    $event
  ) => {
    return !!item.value.date_from;
  };
  const act_5962862420439144_setData_wh0vs9mpn_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.date_from',
      value: $event.data[0],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_5962862420439144_setData_78nj7cr72_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.date_to',
      value: $event.data[1],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_8358196963355853_emit_ycrfzgo0p_getActionArgs = async ($event) => {
    return {
      event: 'submit',
      value: { ...item.value, account: item.value.account?.id },
    };
  };
  const act_6424600534600827_showModal_zbuhsid09_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        accountId: item.value.account ? item.value.account.id : null,
        transactionKind: 'cash_book',
        transactionType: 'deposit',
      },
    };
  };
  const act_8249915319319009_showModal_f4qr6l3eo_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        accountId: item.value.account ? item.value.account.id : null,
        transactionKind: 'cash_book',
        transactionType: 'payment',
      },
    };
  };
  const act_2276125547171722_showModal_4kjtklrzd_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        accountId: item.value.account ? item.value.account.id : null,
        transactionKind:
          selectedAccountType.value === 'bank_account'
            ? 'bank'
            : selectedAccountType.value,
      },
    };
  };
  const act_1836715606397834_showModal_a7cju940x_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ImportBankStatement',
    };
  };

  const act_2248226175642056_emit_dfe1a6vsv = new actionTypes.EmitAction({
    actionArgs: act_2248226175642056_emit_dfe1a6vsv_getActionArgs,
    displayName: 'load',
  });
  const inst_evh_2248226175642056_updateModelValue_2szzvz5ja =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_emit_dfe1a6vsv,
      event: 'update:modelValue',
      when: inst_evh_2248226175642056_updateModelValue_2szzvz5ja_getWhen,
      displayName: 'load',
    });
  const inst_evh_2248226175642056_updateModelValue =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_updateModelValue_2szzvz5ja],
      event: 'update:modelValue',
    });
  const act_5962862420439144_setData_wh0vs9mpn = new actionTypes.SetDataAction({
    actionArgs: act_5962862420439144_setData_wh0vs9mpn_getActionArgs,
  });
  const inst_evh_5962862420439144_changed_a5atrv95o =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_setData_wh0vs9mpn,
      event: 'changed',
      displayName: 'setData',
    });
  const act_5962862420439144_setData_78nj7cr72 = new actionTypes.SetDataAction({
    actionArgs: act_5962862420439144_setData_78nj7cr72_getActionArgs,
  });
  const inst_evh_5962862420439144_changed_60a91wsdu =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_setData_78nj7cr72,
      event: 'changed',
      displayName: 'setData',
    });
  const inst_evh_5962862420439144_changed = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_5962862420439144_changed_a5atrv95o,
      inst_evh_5962862420439144_changed_60a91wsdu,
    ],
    event: 'changed',
  });
  const act_8358196963355853_emit_ycrfzgo0p = new actionTypes.EmitAction({
    actionArgs: act_8358196963355853_emit_ycrfzgo0p_getActionArgs,
    displayName: 'load',
  });
  const inst_evh_8358196963355853_click_3g0rvxpn0 =
    new actionTypes.EventHandlerImpl({
      action: act_8358196963355853_emit_ycrfzgo0p,
      event: 'click',
      displayName: 'load',
    });
  const inst_evh_8358196963355853_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8358196963355853_click_3g0rvxpn0],
    event: 'click',
  });
  const act_6424600534600827_showModal_zbuhsid09 =
    new actionTypes.ShowModalAction({
      actionArgs: act_6424600534600827_showModal_zbuhsid09_getActionArgs,
    });
  const inst_evh_6424600534600827_clickPrevent_g7usd1itl =
    new actionTypes.EventHandlerImpl({
      action: act_6424600534600827_showModal_zbuhsid09,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_6424600534600827_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6424600534600827_clickPrevent_g7usd1itl],
      event: 'click.prevent',
    });
  const act_8249915319319009_showModal_f4qr6l3eo =
    new actionTypes.ShowModalAction({
      actionArgs: act_8249915319319009_showModal_f4qr6l3eo_getActionArgs,
    });
  const inst_evh_8249915319319009_clickPrevent_0lqr3b9xt =
    new actionTypes.EventHandlerImpl({
      action: act_8249915319319009_showModal_f4qr6l3eo,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_8249915319319009_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8249915319319009_clickPrevent_0lqr3b9xt],
      event: 'click.prevent',
    });
  const act_2276125547171722_showModal_4kjtklrzd =
    new actionTypes.ShowModalAction({
      actionArgs: act_2276125547171722_showModal_4kjtklrzd_getActionArgs,
    });
  const inst_evh_2276125547171722_clickPrevent_g8iwu87oi =
    new actionTypes.EventHandlerImpl({
      action: act_2276125547171722_showModal_4kjtklrzd,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_2276125547171722_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2276125547171722_clickPrevent_g8iwu87oi],
      event: 'click.prevent',
    });
  const act_1836715606397834_showModal_a7cju940x =
    new actionTypes.ShowModalAction({
      actionArgs: act_1836715606397834_showModal_a7cju940x_getActionArgs,
    });
  const inst_evh_1836715606397834_clickPrevent_1deywi9ei =
    new actionTypes.EventHandlerImpl({
      action: act_1836715606397834_showModal_a7cju940x,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_1836715606397834_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1836715606397834_clickPrevent_1deywi9ei],
      event: 'click.prevent',
    });

  const actions = {
    inst_evh_2248226175642056_updateModelValue_2szzvz5ja,
    load: inst_evh_2248226175642056_updateModelValue_2szzvz5ja,
    inst_evh_2248226175642056_updateModelValue,
    inst_evh_5962862420439144_changed_a5atrv95o,
    inst_evh_5962862420439144_changed_60a91wsdu,
    inst_evh_5962862420439144_changed,
    inst_evh_8358196963355853_click_3g0rvxpn0,
    inst_evh_8358196963355853_click,
    inst_evh_6424600534600827_clickPrevent_g7usd1itl,
    inst_evh_6424600534600827_clickPrevent,
    inst_evh_8249915319319009_clickPrevent_0lqr3b9xt,
    inst_evh_8249915319319009_clickPrevent,
    inst_evh_2276125547171722_clickPrevent_g8iwu87oi,
    inst_evh_2276125547171722_clickPrevent,
    inst_evh_1836715606397834_clickPrevent_1deywi9ei,
    inst_evh_1836715606397834_clickPrevent,
  };

  const evh_2248226175642056_updateModelValue_2szzvz5ja = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_updateModelValue_2szzvz5ja.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_updateModelValue = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_updateModelValue.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_changed_a5atrv95o = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_changed_a5atrv95o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_changed_60a91wsdu = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_changed_60a91wsdu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_changed = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8358196963355853_click_3g0rvxpn0 = async (event, scope) => {
    return await actions.inst_evh_8358196963355853_click_3g0rvxpn0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8358196963355853_click = async (event, scope) => {
    return await actions.inst_evh_8358196963355853_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6424600534600827_clickPrevent_g7usd1itl = async (event, scope) => {
    return await actions.inst_evh_6424600534600827_clickPrevent_g7usd1itl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6424600534600827_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_6424600534600827_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8249915319319009_clickPrevent_0lqr3b9xt = async (event, scope) => {
    return await actions.inst_evh_8249915319319009_clickPrevent_0lqr3b9xt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8249915319319009_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_8249915319319009_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2276125547171722_clickPrevent_g8iwu87oi = async (event, scope) => {
    return await actions.inst_evh_2276125547171722_clickPrevent_g8iwu87oi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2276125547171722_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_2276125547171722_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1836715606397834_clickPrevent_1deywi9ei = async (event, scope) => {
    return await actions.inst_evh_1836715606397834_clickPrevent_1deywi9ei.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1836715606397834_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_1836715606397834_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center" :gap="2" v-if="loaded">
    <UcAccountSelector
      cacheKey="Payments"
      v-model="item.account"
      @update:modelValue="evh_2248226175642056_updateModelValue($event, {})"
    >
    </UcAccountSelector>
    <DateRangeSelector
      name="paymentFilters"
      :from="item.date_from"
      :to="item.date_to"
      :style="b_5962862420439144_style"
      @changed="evh_5962862420439144_changed($event, {})"
    >
    </DateRangeSelector>
    <BtnLoad
      :action="actions.load"
      @click="evh_8358196963355853_click($event, {})"
    >
    </BtnLoad>
    <Vr class="mx-2"> </Vr>
    <BDropdown
      :text="gettext('Commands')"
      variant="light"
      class="ms-auto border rounded"
    >
      <ItpButton
        :text="pgettext('Payments', 'Add deposit')"
        :icon="$config.payment.icon"
        dropdown-item
        v-if="selectedAccountType && selectedAccountType == 'cash_book'"
        @click.prevent="evh_6424600534600827_clickPrevent($event, {})"
      >
      </ItpButton>
      <ItpButton
        :text="pgettext('Payments', 'New payment')"
        :icon="$config.payment.icon"
        dropdown-item
        v-if="selectedAccountType && selectedAccountType == 'cash_book'"
        @click.prevent="evh_8249915319319009_clickPrevent($event, {})"
      >
      </ItpButton>
      <ItpButton
        :text="pgettext('Payments', 'New transaction...')"
        :icon="$config.payment.icon"
        dropdown-item
        class="me-2"
        v-if="selectedAccountType && selectedAccountType !== 'cash_book'"
        @click.prevent="evh_2276125547171722_clickPrevent($event, {})"
      >
      </ItpButton>
      <ItpButton
        :text="pgettext('Payments', 'Import Bank Statement...')"
        :icon="$config.uploadIcon"
        dropdown-item
        class="me-2"
        @click.prevent="evh_1836715606397834_clickPrevent($event, {})"
      >
      </ItpButton>
    </BDropdown>
  </ItpBox>
</template>
